import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode, ServiceModeStatus } from 'state/service-mode/types';
import { StoreProxy } from 'state/store/hooks';
import { useFlag } from 'utils/launchdarkly';
import {
  getDateNowByTimezone,
  isMobileOrderingAvailable,
  isRestaurantOpen,
  useGetRestaurantAvailabilityFn,
  useGetRestaurantFn,
} from 'utils/restaurant';
import { PICKUP_SERVICE_MODES, isCatering } from 'utils/service-mode';

import { IS_AVAILABLE_CURBSIDE_SERVICE_MODE } from './constants';

export const useServiceModeStatusGenerator = (ignoreFutureOrdering = false) => {
  const isCateringDeliveryEnabled = useFlag('enable-catering-delivery');
  const isCateringEnabled = useFlag('enable-catering');
  const isCurbsideEnabled = useFlag('enable-curbside');
  const isDeliveryEnabled = useFlag('enable-delivery');
  // Move these up in order to check for null (meaning LD service is down) or undefined
  // FYI - use ENABLE_* flags to avoid this necessity as null values will correctly disable the option
  const driveThruDisabled = useFlag('disable-drive-thru');
  const dineInDisabled = useFlag('disable-dine-in');
  const takeOutDisabled = useFlag('disable-take-out');
  const enableFutureOrdering = useFlag('enable-future-ordering');
  const pickupWindowDisabled = useFlag('disable-pickup-window');

  // ENABLE_DELIVERY_CHECKOUT_OUTSIDE_OPENING_HOURS
  const enableDeliveryOutsideOpeningHours = useFlag(
    'enable-delivery-checkout-outside-opening-hours'
  );

  const generateServiceModeStatusForStore = useCallback(
    (store: StoreProxy | IRestaurantNode) => {
      const serviceModeStatus: ServiceModeStatus = {
        CATERING_DELIVERY: {
          capable: !!store.hasDelivery && !!store.hasCatering,
          available: false,
          disabled: !isCateringDeliveryEnabled,
        },
        CATERING_PICKUP: {
          capable: !!store.hasCatering,
          available: false,
          disabled: !isCateringEnabled,
        },
        CURBSIDE: {
          capable: !!store.hasCurbside,
          available: false,
          disabled: !isCurbsideEnabled,
          open: false,
        },
        DELIVERY: {
          capable: !!store.hasDelivery,
          available: false,
          disabled: !isDeliveryEnabled,
          open: false,
        },
        DRIVE_THRU: {
          capable: !!store.hasDriveThru,
          available: false,
          disabled: isNil(driveThruDisabled) || driveThruDisabled,
          open: false,
        },
        EAT_IN: {
          capable: !!store.hasDineIn,
          available: false,
          disabled: isNil(dineInDisabled) || dineInDisabled,
          open: false,
        },
        TAKEOUT: {
          capable: !!store.hasTakeOut,
          available: false,
          disabled: isNil(takeOutDisabled) || takeOutDisabled,
          open: false,
        },
        PICKUP_WINDOW: {
          capable: !!store.hasPickupWindow,
          available: false,
          disabled: pickupWindowDisabled,
          open: false,
        },
      };

      const dateNow = getDateNowByTimezone({
        timeZone: store.timezone || undefined,
      });

      const curbsideOpen = isRestaurantOpen(store.curbsideHours, dateNow);
      const deliveryOpen =
        isRestaurantOpen(store.deliveryHours, dateNow) || enableDeliveryOutsideOpeningHours;
      const diningRoomOpen = isRestaurantOpen(store.diningRoomHours, dateNow);
      const driveThruOpen = isRestaurantOpen(store.driveThruHours, dateNow);
      const pickupWindowOpen = isRestaurantOpen(store.pickupWindowHours, dateNow);

      /** Need to know if service mode is open for store selection 2.0 */
      serviceModeStatus.DELIVERY.open = deliveryOpen;
      serviceModeStatus.EAT_IN.open = diningRoomOpen;
      serviceModeStatus.TAKEOUT.open = diningRoomOpen;
      serviceModeStatus.CURBSIDE.open = curbsideOpen;
      serviceModeStatus.DRIVE_THRU.open = driveThruOpen;
      serviceModeStatus.PICKUP_WINDOW.open = pickupWindowOpen;

      if (enableFutureOrdering && !ignoreFutureOrdering) {
        serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
        serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
        serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable;
        serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable;
        serviceModeStatus.CURBSIDE.available =
          IS_AVAILABLE_CURBSIDE_SERVICE_MODE && serviceModeStatus.CURBSIDE.capable;

        serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable;
        serviceModeStatus.DRIVE_THRU.available = serviceModeStatus.DRIVE_THRU.capable;
        serviceModeStatus.PICKUP_WINDOW.available = serviceModeStatus.PICKUP_WINDOW.capable;
      } else {
        serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
        serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
        serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable && deliveryOpen;
        serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable && diningRoomOpen;
        serviceModeStatus.CURBSIDE.available =
          IS_AVAILABLE_CURBSIDE_SERVICE_MODE && serviceModeStatus.CURBSIDE.capable && curbsideOpen;
        serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable && diningRoomOpen;
        serviceModeStatus.DRIVE_THRU.available =
          serviceModeStatus.DRIVE_THRU.capable && driveThruOpen;
        serviceModeStatus.PICKUP_WINDOW.available =
          serviceModeStatus.PICKUP_WINDOW.capable && pickupWindowOpen;
      }

      return serviceModeStatus;
    },
    [
      isCateringDeliveryEnabled,
      isCateringEnabled,
      isCurbsideEnabled,
      isDeliveryEnabled,
      driveThruDisabled,
      dineInDisabled,
      takeOutDisabled,
      enableDeliveryOutsideOpeningHours,
      enableFutureOrdering,
    ]
  );

  return {
    generateServiceModeStatusForStore,
  };
};

const isAvailableAndNotDisabled = ({
  available,
  disabled,
}: ServiceModeStatus[keyof ServiceModeStatus]) => available && !disabled;

export const useServiceModeStatus = (
  store: StoreProxy | IRestaurantNode,
  ignoreFutureOrdering = false,
  appStateStatus?: string
) => {
  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });
  const validMobileOrderingEnvs = restaurantsConfig.validMobileOrderingEnvs ?? [];
  const { serviceMode } = useServiceModeContext();

  const enableOrdering = useFlag('enable-ordering');
  const storeSelection2_0Enabled = useFlag('enable-store-selection-2-0');

  const hasAvailableProperty = useGetRestaurantAvailabilityFn();
  const getRestaurant = useGetRestaurantFn();
  const { generateServiceModeStatusForStore } = useServiceModeStatusGenerator(ignoreFutureOrdering);

  const serviceModeStatus = useMemo(
    () => generateServiceModeStatusForStore(store),
    // We want appStateStatus here to force re-render when changing app state status
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateServiceModeStatusForStore, store, appStateStatus]
  );

  const allServiceModesUnavailable = Object.values(serviceModeStatus).every(
    ({ available, disabled }) => !available || disabled
  );

  const availablePickupServiceModes = useMemo(
    () => PICKUP_SERVICE_MODES.filter(value => isAvailableAndNotDisabled(serviceModeStatus[value])),
    [serviceModeStatus]
  );

  const isRestaurantAvailable = useCallback(
    async (restaurantData: StoreProxy | IRestaurantNode) => {
      const mobileOrderingEnabled = isMobileOrderingAvailable(
        restaurantData,
        validMobileOrderingEnvs
      );
      const { number: storeId } = restaurantData;

      if (!storeId) {
        return false;
      }

      const rbiRestaurant = await getRestaurant(storeId);

      const isRestaurantPosAvailable = Boolean(rbiRestaurant?.available);

      const open =
        !serviceMode ||
        [ServiceMode.DELIVERY, ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
          serviceMode
        ) ||
        (serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled);

      return open && isRestaurantPosAvailable && mobileOrderingEnabled;
    },
    [serviceMode, serviceModeStatus] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Determines whether a restaurant can be selected */
  const restaurantCanBeSelected = useMemo(() => {
    // Store selection 2.0: any store can be selected
    if (storeSelection2_0Enabled && store._id) {
      return true;
    }

    if (!enableOrdering) {
      return false;
    }

    /*
     * Catering service mode is preselected and assumed to be available for
     * ordering during selection.
     */
    if (isCatering(serviceMode)) {
      return true;
    }

    // Store selection 1.0: store can only be selected if there is an available service mode
    return (
      hasAvailableProperty(store) &&
      isMobileOrderingAvailable(store, validMobileOrderingEnvs) &&
      Boolean(availablePickupServiceModes.length)
    );
  }, [
    storeSelection2_0Enabled,
    store,
    enableOrdering,
    serviceMode,
    hasAvailableProperty,
    validMobileOrderingEnvs,
    availablePickupServiceModes.length,
  ]);

  return {
    allServiceModesUnavailable,
    availablePickupServiceModes,
    isRestaurantAvailable,
    restaurantCanBeSelected,
    serviceModeStatus,
  };
};
